import type { components } from "@countdown/api-client";
import { Plus } from "lucide-react";
import { type ReactElement, type SyntheticEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { FormField } from "@/components/FormField";
import { LinkRow } from "@/components/LinkRow";
import { OutcomeNotice } from "@/components/Notice";
import { PageHeading } from "@/components/PageHeading";
import { Section } from "@/components/Section";
import { ViewTabs } from "@/components/ViewTabs";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type BackofficeBrand,
  LOAD_FAILED,
  createAction,
  createCustomerAction,
  loadBrands,
  useResource,
} from "@/lib/api";
import { NUMBER_FORMAT, can, formatDate, notArchived } from "@/lib/labels";
import { ActionBadges } from "@/pages/ActionPage";

type ActionView = components["schemas"]["BackofficeActionView"];
type ParticipantStats = components["schemas"]["ParticipantStatsView"];
type SponsorView = components["schemas"]["BackofficeSponsorView"];

/**
 * The browser's own list of zone names, offered as suggestions; the server checks the value.
 * Guarded: an older browser without the list would otherwise fail the whole bundle at load.
 */
export const TIME_ZONES: readonly string[] =
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : ["Europe/Berlin"];

/** A figure the way the house style sets one: the number large in Verlag. */
function StatCard({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number;
}): ReactElement {
  return (
    <Card className="border-0 bg-surface-sunken shadow-none">
      <CardHeader>
        <CardDescription className="font-sans text-sm text-foreground">{label}</CardDescription>
        <CardTitle className="font-sans text-3xl font-bold tabular-nums">
          {NUMBER_FORMAT.format(value)}
        </CardTitle>
      </CardHeader>
    </Card>
  );
}

const LEGEND = "text-xs font-medium tracking-wider text-muted-foreground uppercase";
const FIELD_GROUP = "grid gap-x-6 sm:grid-cols-2";

/**
 * The id of a new action, and the step to its settings once the reloaded overview holds it:
 * the action page finds an action in the overview, so navigating straight away would show
 * "not found" for a moment.
 */
function useOpenWhenListed(
  actions: readonly ActionView[],
): readonly [string | null, (id: string) => void] {
  const navigate = useNavigate();
  const [createdId, setCreatedId] = useState<string | null>(null);
  useEffect(() => {
    if (createdId !== null && actions.some((action) => action.action_id === createdId)) {
      void navigate(`/aktionen/${createdId}/einstellungen`);
    }
  }, [actions, createdId, navigate]);
  return [createdId, setCreatedId];
}

/** One required choice of the dialog. */
function Choice({
  id,
  label,
  placeholder,
  options,
  disabled,
  onValueChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly options: readonly (readonly [string, string])[];
  readonly disabled: boolean;
  readonly onValueChange?: (value: string) => void;
}): ReactElement {
  return (
    <div className="mt-4 grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select name={id} required disabled={disabled} onValueChange={onValueChange}>
        <SelectTrigger id={id} className="w-full bg-background">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(([value, text]) => (
            <SelectItem key={value} value={value}>
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** The two calendar kinds (E4): doors booked by sponsors, or every door one customer's. */
export type CalendarKind = "sponsors" | "customer";

const KIND_LABELS: Readonly<Record<CalendarKind, string>> = {
  sponsors: "Mit Sponsoren",
  customer: "Für einen Kunden",
};

/**
 * The kinds this account may create. A customer's calendar needs the door elements too: its
 * route books and releases every door, so its own element must not bypass them.
 */
export function calendarKinds(granted: readonly string[]): readonly CalendarKind[] {
  return [
    ...(can(granted, "action_create") ? (["sponsors"] as const) : []),
    ...(can(granted, "customer_action") &&
    can(granted, "door_placement") &&
    can(granted, "door_review")
      ? (["customer"] as const)
      : []),
  ];
}

/** The value of the customer choice that opens the fields of a new firm. */
const NEW_CUSTOMER = "neu";

/**
 * „Aktion anlegen": the calendar kind first, then its fields (E4).
 *
 * „Mit Sponsoren" is a new draft with its doors and deadlines. „Für einen Kunden" is a copy
 * of a template whose every door the customer books, the template's content visible from
 * creation; only templates whose doors are all the organizer's own. The customer is an
 * existing firm or, with the sponsors page's element, a new one. No deadlines: the customer
 * publishes their doors themselves.
 *
 * The brand logo is not uploaded here: it comes with the brand from the catalog of the
 * customer portal (D25), or from the master data page without one.
 */
function CreateAction({
  actions,
  sponsors,
  brandNoun,
  kinds,
  newCustomer,
  reloadOverview,
}: {
  readonly actions: readonly ActionView[];
  readonly sponsors: readonly SponsorView[];
  /** "Zeitschrift" with a brand catalog (Pflaum), "Marke" without. */
  readonly brandNoun: string;
  readonly kinds: readonly CalendarKind[];
  /** Whether a new firm may be created here (the sponsors page's element). */
  readonly newCustomer: boolean;
  readonly reloadOverview: () => void;
}): ReactElement {
  const brands = useResource<BackofficeBrand[]>(loadBrands);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [createdId, setCreatedId] = useOpenWhenListed(actions);
  const onlyKind = kinds.length === 1 ? (kinds[0] ?? null) : null;
  const [kind, setKind] = useState<CalendarKind | null>(onlyKind);
  const [customer, setCustomer] = useState("");

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = (name: string): string => {
      const raw = form.get(name);
      return typeof raw === "string" ? raw.trim() : "";
    };
    const days = (name: string): number | null =>
      value(name) === "" ? null : Number(value(name));
    const basics = {
      brand_key: value("brand_key"),
      title: value("title"),
      stable_link_slug: value("stable_link_slug"),
    };
    setBusy(true);
    setNotice(null);
    const request =
      kind === "customer"
        ? createCustomerAction({
            ...basics,
            template_action_id: value("template_action_id"),
            ...(customer === NEW_CUSTOMER
              ? {
                  new_customer: {
                    company_name: value("company_name"),
                    contact_name: value("contact_name") === "" ? null : value("contact_name"),
                    contact_email: value("contact_email"),
                  },
                }
              : { sponsor_id: customer }),
          })
        : createAction({
            ...basics,
            starts_on: value("starts_on"),
            ends_on: value("ends_on"),
            field_count: Number(value("field_count")),
            timezone: value("timezone"),
            followup_period_days: Number(value("followup_period_days")),
            submission_lead_days: days("submission_lead_days"),
            approval_lead_days: days("approval_lead_days"),
            minimum_booking_deadline:
              value("minimum_booking_deadline") === "" ? null : value("minimum_booking_deadline"),
          });
    request
      .then((result) => {
        if (result.ok) {
          setCreatedId(result.detail.action_id);
        } else {
          setNotice(result.message);
        }
        // Also after a refusal: a customer's picture that failed to copy leaves the action.
        reloadOverview();
      })
      .catch((error: unknown) => {
        console.error("[ActionsPage] action create failed", error);
        setNotice(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const locked = busy || createdId !== null;
  const brandList = (brands.status === "ready" ? brands.data : []).map(
    (brand) => [brand.brand_key, brand.name] as const,
  );
  const templates = actions
    .filter((action) => action.own_doors_only)
    .map((action) => [action.action_id, action.title] as const);
  const customers = [
    ...sponsors
      .filter((sponsor) => !sponsor.is_own && sponsor.status !== "inactive")
      .map((sponsor) => [sponsor.sponsor_id, sponsor.company_name] as const),
    ...(newCustomer ? [[NEW_CUSTOMER, "Neuer Kunde"] as const] : []),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setNotice(null);
        setKind(onlyKind);
        setCustomer("");
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus aria-hidden="true" />
          Aktion anlegen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Aktion anlegen</DialogTitle>
          <DialogDescription>
            {kind === "customer"
              ? "Alle Türchen gehören dem Kunden und zeigen ab Anlage den Inhalt der Vorlage."
              : "Die Aktion entsteht als Entwurf mit allen Türchen, danach geht es in den Einstellungen weiter."}
          </DialogDescription>
        </DialogHeader>
        <form id="aktion-neu" onSubmit={submit}>
          {kinds.length > 1 ? (
            <fieldset className="mb-6" disabled={locked}>
              <legend className={LEGEND}>Kalenderart</legend>
              <div className="mt-3 grid gap-2">
                {kinds.map((value) => (
                  <div key={value} className="flex items-center gap-2">
                    <input
                      id={`kalenderart-${value}`}
                      type="radio"
                      name="kalenderart"
                      value={value}
                      checked={kind === value}
                      onChange={() => {
                        setKind(value);
                        setCustomer("");
                      }}
                    />
                    <label className="text-sm" htmlFor={`kalenderart-${value}`}>
                      {KIND_LABELS[value]}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          ) : null}
          {kind === null ? null : (
            <fieldset disabled={locked}>
              <legend className={LEGEND}>Grunddaten</legend>
              {kind === "customer" ? (
                <>
                  <Choice
                    id="template_action_id"
                    label="Vorlage"
                    placeholder={
                      templates.length === 0 ? "Keine passende Vorlage" : "Vorlage wählen"
                    }
                    options={templates}
                    disabled={locked}
                  />
                  <Choice
                    id="sponsor_id"
                    label="Kunde"
                    placeholder={
                      customers.length === 0 ? "Erst unter »Sponsoren« anlegen" : "Kunde wählen"
                    }
                    options={customers}
                    disabled={locked}
                    onValueChange={setCustomer}
                  />
                  {customer === NEW_CUSTOMER ? (
                    <div className={FIELD_GROUP}>
                      <FormField label="Firma" name="company_name" required maxLength={200} />
                      <FormField label="Ansprechpartner" name="contact_name" maxLength={200} />
                      <FormField
                        label="E-Mail für die Einladung"
                        name="contact_email"
                        type="email"
                        required
                        maxLength={320}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              <Choice
                id="brand_key"
                label={brandNoun}
                placeholder={brands.status === "loading" ? "Wird geladen …" : `${brandNoun} wählen`}
                options={brandList}
                disabled={locked}
              />
              <div className={FIELD_GROUP}>
                <FormField label="Titel" name="title" required maxLength={200} />
                <FormField
                  label="Link"
                  name="stable_link_slug"
                  required
                  maxLength={120}
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  placeholder={kind === "customer" ? "advent-2026-kunde" : "advent-2026"}
                />
              </div>
            </fieldset>
          )}
          {kind === "sponsors" ? (
            <>
              <fieldset className="mt-6" disabled={locked}>
                <legend className={LEGEND}>Zeitraum</legend>
                <div className={FIELD_GROUP}>
                  <FormField label="Beginn" name="starts_on" type="date" required />
                  <FormField label="Ende" name="ends_on" type="date" required />
                  <FormField
                    label="Anzahl Türchen"
                    name="field_count"
                    type="number"
                    required
                    min={1}
                    max={366}
                    defaultValue={24}
                  />
                  <FormField
                    label="Zeitzone"
                    name="timezone"
                    required
                    list="zeitzonen"
                    defaultValue="Europe/Berlin"
                  />
                </div>
                <datalist id="zeitzonen">
                  {TIME_ZONES.map((zone) => (
                    <option key={zone} value={zone} />
                  ))}
                </datalist>
              </fieldset>
              <fieldset className="mt-6" disabled={locked}>
                <legend className={LEGEND}>Fristen</legend>
                <div className={FIELD_GROUP}>
                  <FormField
                    label="Nachlauffrist (Tage)"
                    name="followup_period_days"
                    type="number"
                    required
                    min={0}
                    max={365}
                    defaultValue={14}
                  />
                  <FormField
                    label="Mindestbelegung bis"
                    name="minimum_booking_deadline"
                    type="date"
                  />
                  <FormField
                    label="Einreichfrist (Tage vor Öffnung)"
                    name="submission_lead_days"
                    type="number"
                    min={0}
                    max={365}
                  />
                  <FormField
                    label="Freigabefrist (Tage vor Öffnung)"
                    name="approval_lead_days"
                    type="number"
                    min={0}
                    max={365}
                  />
                </div>
              </fieldset>
            </>
          ) : null}
        </form>
        {notice !== null ? <OutcomeNotice outcome={{ ok: false, message: notice }} /> : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Abbrechen
          </Button>
          <Button type="submit" form="aktion-neu" disabled={locked || kind === null}>
            {locked ? "Wird angelegt …" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The tabs of the action list, as the firms have them (E8), and the archive (02.11.2026). */
export type ActionTab = "sponsors" | "customers" | "archive";

const ACTION_TABS: Readonly<Record<ActionTab, { readonly label: string; readonly to: string }>> = {
  sponsors: { label: "Sponsoren", to: "/" },
  customers: { label: "Kunden", to: "/?ansicht=kunden" },
  archive: { label: "Archiv", to: "/?ansicht=archiv" },
};

/** The tab of `?ansicht=`; the archive is the superadmin's, anybody else lands on sponsors. */
export function actionTab(search: URLSearchParams, superadmin: boolean): ActionTab {
  const view = search.get("ansicht");
  if (view === "kunden") {
    return "customers";
  }
  return view === "archiv" && superadmin ? "archive" : "sponsors";
}

/** The actions of one tab: archived ones only in the archive, the others by calendar kind. */
export function actionsInView(actions: readonly ActionView[], tab: ActionTab): ActionView[] {
  return actions.filter((action) =>
    tab === "archive"
      ? !notArchived(action)
      : notArchived(action) &&
        ((action.customer_sponsor_id ?? null) !== null) === (tab === "customers"),
  );
}

interface ActionsPageProps {
  readonly actions: readonly ActionView[];
  readonly participants: ParticipantStats;
  readonly sponsors: readonly SponsorView[];
  readonly granted: readonly string[];
  /** The archive tab is the superadmin's (02.11.2026). */
  readonly superadmin: boolean;
  /** Whether the brands come from a catalog (from the session); Pflaum's do. */
  readonly brandCatalog: boolean;
  readonly reloadOverview: () => void;
}

/**
 * Overview of the actions of this cell plus the participant aggregates.
 *
 * Data minimisation (concept 8.3, 10.6): this screen shows AGGREGATES only.
 * There is deliberately no participant list: names, addresses and e-mail
 * addresses are never loaded into an overview.
 */
export function ActionsPage({
  actions,
  participants,
  sponsors,
  granted,
  superadmin,
  brandCatalog,
  reloadOverview,
}: ActionsPageProps): ReactElement {
  const kinds = calendarKinds(granted);
  const brandNoun = brandCatalog ? "Zeitschrift" : "Marke";
  const [search] = useSearchParams();
  const tab = actionTab(search, superadmin);
  const shown = actionsInView(actions, tab);
  const tabs = [
    ACTION_TABS.sponsors,
    ACTION_TABS.customers,
    ...(superadmin ? [ACTION_TABS.archive] : []),
  ];

  return (
    <>
      <PageHeading
        title="Aktionen"
        actions={
          kinds.length === 0 || tab === "archive" ? null : (
            <CreateAction
              // No archived template: it is gone until the superadmin brings it back.
              actions={actions.filter(notArchived)}
              sponsors={sponsors}
              brandNoun={brandNoun}
              kinds={kinds}
              newCustomer={can(granted, "sponsor_master")}
              reloadOverview={reloadOverview}
            />
          )
        }
      />
      <ViewTabs label="Aktionen" tabs={tabs} current={ACTION_TABS[tab].label} />

      {/* The actions first: they are what this page is opened for. The figures follow. */}
      <Section>
        <Table>
          <TableCaption className="sr-only">Aktionen</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Titel</TableHead>
              <TableHead>{brandNoun}</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Zeitraum</TableHead>
              <TableHead className="text-center">Belegte Türchen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((action) => (
              <LinkRow key={action.action_id}>
                <TableCell className="min-w-48 font-medium">
                  <Link
                    className="underline underline-offset-4"
                    to={`/aktionen/${action.action_id}`}
                  >
                    {action.title}
                  </Link>
                </TableCell>
                <TableCell>{action.brand_name}</TableCell>
                <TableCell>
                  <ActionBadges action={action} />
                </TableCell>
                <TableCell>
                  {formatDate(action.starts_on)} bis {formatDate(action.ends_on)}
                </TableCell>
                <TableCell className="text-center">
                  {action.booked_fields} von {action.field_count}
                </TableCell>
              </LinkRow>
            ))}
            {shown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                  {tab === "archive" ? "Keine archivierte Aktion." : "Noch keine Aktion."}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Section>

      <Section title="Teilnahme insgesamt">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label="Registrierte Konten" value={participants.registered_accounts} />
          <StatCard label="Bestätigte E-Mails" value={participants.confirmed_addresses} />
          <StatCard label="Teilnahmen" value={participants.participations} />
          <StatCard label="Offene Entwürfe" value={participants.open_drafts} />
          <StatCard label="Unterdrückte E-Mails" value={participants.suppressed} />
        </div>
      </Section>
    </>
  );
}
